import { useMemo, useRef } from "react";
import { useCollection } from "react-firebase-hooks/firestore";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  QuerySnapshot,
  DocumentData,
} from "firebase/firestore";

import { db } from "../../utils/firebase";

const chatMessagesRef = collection(db, "chatMessages");

export function useChatMessages(partyId: string | null, pageSize: number) {
  const [value, loading, error] = useCollection(
    partyId
      ? query(
          chatMessagesRef,
          where("partyId", "==", partyId),
          orderBy("created", "desc"),
          limit(pageSize)
        )
      : null
  );

  if (error) {
    // eslint-disable-next-line no-console
    console.error("useChatMessages error:", error);
  }

  // `useCollection` clears `value` to undefined while it re-subscribes when
  // the query changes (e.g. on load-more). Hold the previous snapshot so the
  // rendered list doesn't flicker empty during that window.
  const stableValueRef = useRef<QuerySnapshot<DocumentData> | undefined>(
    undefined
  );
  if (value) stableValueRef.current = value;
  const effectiveValue = value ?? stableValueRef.current;

  const messages: ChatMessage[] = useMemo(() => {
    if (!effectiveValue) return [];
    // Fetched newest-first; reverse for ascending display. Drop docs that
    // don't yet have a server-resolved `created` (rare, but happens for the
    // briefest moment after a local write).
    const list = effectiveValue.docs
      .map((d) => ({ ...d.data(), _docId: d.id } as ChatMessage))
      .filter((m) => m.created != null);
    return list.reverse();
  }, [effectiveValue]);

  // If we got a full page back, there is probably more history available.
  const hasMore = (effectiveValue?.docs.length ?? 0) >= pageSize;

  return { messages, loading, hasMore, error };
}
